import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { Facebook, Instagram } from "lucide-react";
import { LogoComplete, LogoSimple } from "@/components/Logo";
import { CookieStatus } from "@/components/CookieStatus";

export function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-gray-100 text-gray-800 py-16" style={{ minHeight: '400px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-1">
            <div className="mb-4">
              <LogoSimple size="lg" />
            </div>
            <p className="text-gray-600 mb-6 max-w-md">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              {/* TikTok */}
              <a
                href="https://www.tiktok.com/@statemia_es"
                className="text-gray-600 hover:text-gray-800 transition-colors"
                aria-label="TikTok"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              
              {/* Instagram */}
              <a
                href="https://www.instagram.com/statemia"
                className="text-gray-600 hover:text-gray-800 transition-colors"
                aria-label="Instagram"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Instagram className="w-5 h-5" />
              </a>
              
              {/* Facebook */}
              <a
                href="https://www.facebook.com/statemia"
                className="text-gray-600 hover:text-gray-800 transition-colors"
                aria-label="Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook className="w-5 h-5" />
              </a>
              
              {/* X (Twitter) */}
              <a
                href="https://twitter.com/statemia_es"
                className="text-gray-600 hover:text-gray-800 transition-colors"
                aria-label="X (Twitter)"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            </div>
            
            {/* Copyright moved up */}
            <div className="mt-6 text-gray-600 text-sm">
              {t('footer.copyright')}
            </div>
          </div>

          {/* Categories */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">{t('statistics.categories')}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/statistics-list?category=politics">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors capitalize">
                    {t('statistics.politics')}
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/statistics-list?category=society">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors capitalize">
                    {t('statistics.society')}
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/statistics-list?category=economy">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors capitalize">
                    {t('statistics.economy')}
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/statistics-list?category=culture">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors capitalize">
                    {t('statistics.culture')}
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/statistics-list?category=technology">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors capitalize">
                    {t('statistics.technology')}
                  </span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-4">{t('footer.legal')}</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/blog">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors">Blog</span>
                </Link>
              </li>
              <li>
                <Link href="/sobre-statemia">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors">
                    {t('footer.aboutUs')}
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/contacto">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors">
                    {t('footer.contact')}
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/aviso-legal">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors">
                    {t('footer.legalNotice')}
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/politica-de-privacidad">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors">
                    {t('footer.privacyPolicy')}
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/terminos-de-servicio">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors">
                    {t('footer.termsOfService')}
                  </span>
                </Link>
              </li>
              <li>
                <Link href="/politica-de-cookies">
                  <span className="text-gray-600 hover:text-gray-800 transition-colors">
                    {t('footer.cookiePolicy')}
                  </span>
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Cookie Status */}
        <div className="mt-8 pt-8 border-t border-gray-200 flex justify-between items-center">
          <p className="text-gray-600 text-sm">
            {t('footer.copyright')}
          </p>
          <div className="relative">
            <CookieStatus />
          </div>
        </div>

      </div>
    </footer>
  );
}
